import type { Student, Group, Settings, Constraints } from "../../types/models";
import { scoreCandidate } from "../scoring";
import { buildApartPairs } from "../constraints";
import { shuffle, shuffleSeeded } from "../random";
import { findLeastFullGroup } from "./utils";
import { chooseColor } from "../../utils/colors";

/**
 * Greedy placement of students into groups
 * Places each student in the group that scores highest
 * @param rng - Optional seeded random function
 */
export function greedyPlacement(
    students: Student[],
    groups: Group[],
    constraints: Constraints,
    settings: Settings,
    rng?: () => number
): void {
    // Build must-apart lookup
    const apartPairs = buildApartPairs(constraints);

    // Calculate class statistics
    const classMean = students.reduce((sum, s) => sum + s.skill, 0) / Math.max(1, students.length);
    const classStats = { meanSkill: classMean };

    // Get students who aren't placed yet (and aren't locked to a group)
    const placedIds = new Set(groups.flatMap(g => g.memberIds));
    const unplaced = students.filter(s => !placedIds.has(s.id) && !s.lockedToGroupId);

    // Shuffle for randomness (use seeded if provided)
    if (rng) {
        shuffleSeeded(unplaced, rng);
    } else {
        shuffle(unplaced);
    }

    // Student ID to Student map
    const idToStudent = new Map(students.map(s => [s.id, s]));

    for (const student of unplaced) {
        let bestGroup: Group | null = null;
        let bestScore = -Infinity;

        for (const group of groups) {
            // Check capacity
            if (group.memberIds.length >= settings.maxSize) continue;

            // Check must-apart constraints
            if (violatesApart(student.id, group, apartPairs)) continue;

            // Get group members
            const members = group.memberIds
                .map(id => idToStudent.get(id))
                .filter((s): s is Student => s !== undefined);

            // Score this placement
            const score = scoreCandidate(student, members, classStats, settings, settings.targetSize);

            if (score > bestScore) {
                bestScore = score;
                bestGroup = group;
            }
        }

        if (bestGroup) {
            // Place in best group
            bestGroup.memberIds.push(student.id);
        } else {
            // No valid group found - try to create new group or force into least full
            if (!settings.maxGroups || groups.length < settings.maxGroups) {
                // Create new group
                const newGroup: Group = {
                    id: `g${groups.length + 1}`,
                    memberIds: [student.id],
                    color: chooseColor(groups.length)
                };
                groups.push(newGroup);
            } else {
                // Force into least full group (even if it violates apart)
                const leastFull = findLeastFullGroup(groups);
                if (leastFull && leastFull.memberIds.length < settings.maxSize) {
                    leastFull.memberIds.push(student.id);
                }
            }
        }
    }
}

/**
 * Check if placing student in group violates must-apart constraints
 */
function violatesApart(studentId: string, group: Group, apartPairs: Set<string>): boolean {
    for (const memberId of group.memberIds) {
        const [a, b] = [studentId, memberId].sort();
        if (apartPairs.has(`${a}|${b}`)) {
            return true;
        }
    }
    return false;
}

